import {
  creatorListingsPath,
  destinationsPath,
  explorePath,
  favoritesDestinationsPath,
  favoritesPath,
  favoritesProgramsPath,
  loginPath,
  marketplaceIndexPath,
  menuPath,
  messagesPath,
  programsPath,
  rootPath,
} from "@travily/routes";

/**
 * Single source of truth for which routes the mobile bottom tab bar treats as "root".
 *
 * The bottom bar is global navigation: it belongs on the tab destinations and their
 * sub-sections, not on detail/flow pages pushed off a tab (a product, a checkout, an
 * editor). `PageFrame` reads {@link isRootRoute} to decide whether to mount the bar at
 * all, and `AppBottomBar` reads {@link isExploreSection} to decide when the Explore tab
 * lights up. Both derive from the same definitions below so the two never drift.
 *
 * Membership is an exact path match (query string stripped). That distinguishes an index
 * like `/destinations` (root → show the bar) from its detail `/destinations/:c/:slug`
 * (not root → hide the bar).
 */

/** Routes that live under the Explore tab — its home plus the browse sub-sections (the ones the
 *  Explore mobile sub-nav links to: Programs, Destinations, Marketplace). */
const EXPLORE_ROUTES = [rootPath(), explorePath(), destinationsPath(), programsPath(), marketplaceIndexPath()];

/** A profile root (`/@handle`) or its store tab (`/@handle/store`) — not other `/@handle/...` sub-pages. */
const PROFILE_ROUTE = /^\/@[^/]+(\/store)?$/;

interface RootRouteContext {
  mode: "explorer" | "creator";
  isAuthenticated: boolean;
}

function stripQuery(path: string): string {
  return path.split("?")[0] ?? path;
}

/** Whether `path` is the Explore tab's home or one of its browse sub-sections. */
export function isExploreSection(path: string): boolean {
  return EXPLORE_ROUTES.includes(stripQuery(path));
}

/**
 * Whether `path` is `href` or one of its descendants — drives `aria-current` / active styling on
 * nav links (bottom bar, explore sub-nav). Shared so the bar and sub-nav agree on what "active" means.
 */
export function isNavActive(path: string, href: string): boolean {
  const current = stripQuery(path);
  return current === href || current.startsWith(`${href}/`);
}

/**
 * Whether `path` is a bottom-tab destination for the given auth state and mode — i.e. a
 * page the mobile tab bar should appear on. Mirrors the tab sets rendered by
 * `AppBottomBar`.
 */
export function isRootRoute(path: string, { mode, isAuthenticated }: RootRouteContext): boolean {
  const current = stripQuery(path);

  if (!isAuthenticated) {
    return [...EXPLORE_ROUTES, loginPath()].includes(current);
  }

  if (mode === "creator") {
    return [creatorListingsPath(), messagesPath(), menuPath()].includes(current);
  }

  return (
    [...EXPLORE_ROUTES, favoritesPath(), favoritesDestinationsPath(), favoritesProgramsPath(), menuPath()].includes(
      current,
    ) || PROFILE_ROUTE.test(current)
  );
}
